import { Link } from 'react-router-dom'

import { EntwurfIcon, KartenstiftIcon } from '../../components/Icons'
import type { Trip } from '../../api/types'
import { useTexte } from '../../texte'

/**
 * Chat, Karte — und für den Reisenden die Werkbank.
 *
 * Chat und Karte sind zwei gleichrangige Ansichten derselben Reise: beides ist
 * der Bericht, nur anders erzählt. Die Reiseseite (`/info`) steht daneben und
 * bleibt, wo sie ist — sie ist Verwaltung und keine Ansicht.
 *
 * **„Bearbeiten" ist der dritte Reiter und nur für den Reisenden da.** Die
 * Kartenseite trug lange zwei Rollen auf einmal; bei 66 Highlights stand einem
 * Mitlesenden das Werkzeug im Weg, das er ohnehin nicht anfassen darf. Ob es
 * erscheint, sagt der Server (`may_edit`) — geraten wird das nicht.
 *
 * **Seit 0.46.0 eine schwebende Pille auf dem Inhalt, nicht mehr eine eigene
 * Zeile unter der Kopfzeile.** Hier stand: „Unter der Kopfzeile statt darin:
 * auf einem Telefon ist der Kopf schon voll." Das stimmt weiterhin — nur
 * kostete die eigene Zeile 64 Punkte, und mit Kopfzeile begann der Verlauf
 * erst bei 129. Aus dem Betrieb kam der Wunsch, die Umschaltung
 * platzsparender zu machen; am Mock-up wurde die Pille gewählt: oben rechts
 * auf dem Inhalt, milchiges Material wie die Blätter, 36 Punkte sichtbar und
 * 44 Trefferfläche. Im Chat weicht sie beim Scrollen und kommt beim Anhalten
 * zurück (`versteckt`), damit sie beim Lesen nach oben nichts verdeckt; auf
 * der Karte bleibt sie stehen. Die Werkbank ist ein Stift ohne Wort — drei
 * Wörter in einer Pille wären auf 390 Punkten breiter als die halbe Karte.
 *
 * **Sie liegt in einem positionierten Rahmen der Seite** (`.chatflaeche`,
 * `.reiseinhalt`), nicht am Fenster: seit dem milchigen Material (#8) ist
 * jedes `backdrop-filter`-Element ein Bezugsrahmen für `position: fixed`,
 * und absolut im Rahmen ist ohnehin das, was gemeint ist.
 *
 * **And a fourth field since 1.21.0, the draft of the report** (#85) -- for
 * the traveller, when the trip has the draft switched on (`zeigtEntwurf`).
 * The pencil moved there: a pencil says "write". The workbench got a pin
 * with a pencil, because "edit the map" has to say "map" as well. Two words
 * and two symbols measure 233 points at 390 (`entwurf.mjs`) -- more than the
 * half the comment above warns of, and still clear of the bubbles' words.
 */
export function Reiseumschalter({
  tripId,
  aktiv,
  darfBearbeiten = false,
  entwurf = false,
  versteckt = false,
  wartend,
}: {
  tripId: number
  aktiv: 'chat' | 'karte' | 'bearbeiten' | 'entwurf'
  darfBearbeiten?: boolean
  /** The fourth field, the draft of the report (#85). */
  entwurf?: boolean
  /** Weggeklappt, solange der Verlauf darunter scrollt. */
  versteckt?: boolean
  /**
   * Where unsent input waits (1.22.0, `useWartend`): a dot on that field.
   * The input line keeps its content per place; the dot says that
   * something lies over there.
   */
  wartend?: { chat: boolean; entwurf: boolean }
}) {
  const t = useTexte().karte.umschalter
  const feld = (wohin: typeof aktiv) =>
    aktiv === wohin ? 'umschalter__feld umschalter__feld--aktiv' : 'umschalter__feld'
  const punkt = <span className="umschalter__punkt" aria-hidden="true" data-umschalter-punkt="" />

  return (
    <nav
      className={'umschalter umschalter--schwebend' + (versteckt ? ' umschalter--versteckt' : '')}
      aria-label={t.ansicht}
      data-umschalter={versteckt ? 'versteckt' : 'sichtbar'}
    >
      <Link
        className={feld('chat')}
        to={`/t/${tripId}`}
        aria-current={aktiv === 'chat' ? 'page' : undefined}
        aria-label={wartend?.chat ? t.wartend(t.chat) : undefined}
        data-umschalter-ziel="chat"
      >
        {t.chat}
        {wartend?.chat && punkt}
      </Link>
      <Link
        className={feld('karte')}
        to={`/t/${tripId}/karte`}
        aria-current={aktiv === 'karte' ? 'page' : undefined}
        data-umschalter-ziel="karte"
      >
        {t.karte}
      </Link>
      {darfBearbeiten && (
        <Link
          className={feld('bearbeiten') + ' umschalter__feld--symbol'}
          to={`/t/${tripId}/karte/bearbeiten`}
          aria-current={aktiv === 'bearbeiten' ? 'page' : undefined}
          aria-label={t.bearbeiten}
          title={t.bearbeiten}
          data-umschalter-ziel="bearbeiten"
        >
          <KartenstiftIcon size={18} />
        </Link>
      )}
      {entwurf && (
        <Link
          className={feld('entwurf') + ' umschalter__feld--symbol'}
          to={`/t/${tripId}/entwurf`}
          aria-current={aktiv === 'entwurf' ? 'page' : undefined}
          aria-label={wartend?.entwurf ? t.wartend(t.entwurf) : t.entwurf}
          title={t.entwurf}
          data-umschalter-ziel="entwurf"
        >
          <EntwurfIcon size={18} />
          {wartend?.entwurf && punkt}
        </Link>
      )}
    </nav>
  )
}

/**
 * Whether the pill shows the draft (#85) -- derived once, for the chat and
 * the map alike (CLAUDE.md: two sources for one question flicker).
 *
 * On while the switch is on, **or** while drafts still wait: switching off
 * must not strand what was written before.
 */
export function zeigtEntwurf(trip: Trip | undefined): boolean {
  return trip !== undefined && trip.role === 'sender' && (trip.draft_chat || (trip.draft_count ?? 0) > 0)
}
